import { useMutation } from '@apollo/client';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { SAVE_PAYMENT_REQUEST } from '@src/graphql/queries/paymentRequest';
import { useAuth } from '@src/hooks/useAuth';
import { useCurrentClientConfig } from '@src/hooks/useCurrentClientConfig';
import { PaymentMethod, PaymentRequestItemStatus, RuleType, } from '@src/types';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { formatMessageDates } from '@src/utilities/messagesHelper';
import { checkEligibilityLock, isWithinDocumentDeadline, validatePaymentRequest, validatePaymentRequestItem, } from '@src/utilities/paymentRequestHelper';
import { compact, isEmpty, omit, partition, uniqueId } from 'lodash';
import { ContentType, MessageType, useContents, useLocale } from 'pe-core-frontend';
import { useContext, useMemo, useState } from 'react';
export var PaymentRequestStep;
(function (PaymentRequestStep) {
    PaymentRequestStep["ABOUT"] = "about";
    PaymentRequestStep["EXPENSES"] = "expenses";
    PaymentRequestStep["REVIEW"] = "review";
    PaymentRequestStep["UPLOAD_PROOF"] = "upload_proof";
})(PaymentRequestStep || (PaymentRequestStep = {}));
export const useSavePaymentRequest = ({ initialPaymentRequest }) => {
    const { t, formatCurrency } = useLocale();
    const { currentUser } = useAuth();
    const { getPolicy, program } = useContext(ProgramContext);
    const { clientConfig } = useCurrentClientConfig();
    const expensePolicy = getPolicy(RuleType.EXPENSE, program);
    const [itemErrors, setItemErrors] = useState({});
    const [requestErrors, setRequestErrors] = useState({});
    const [itemToEdit, setItemToEdit] = useState(null);
    const [savePaymentRequest] = useMutation(SAVE_PAYMENT_REQUEST);
    const [errors, setErrors] = useState([]);
    const [paymentRequest, setPaymentRequest] = useState(initialPaymentRequest);
    const requiresPreapprovalAttestation = useMemo(() => {
        if (paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT)
            return false;
        const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
        const approvalPolicyConfig = approvalPolicy?.paymentRequestApproval;
        if (!approvalPolicyConfig)
            return false;
        return (approvalPolicyConfig.enableWorkflow &&
            approvalPolicyConfig.workflowId &&
            !isWithinDocumentDeadline(paymentRequest, approvalPolicy, t).success);
    }, [getPolicy, program, paymentRequest]);
    const { contents: attestationContents } = useContents({
        clientId: paymentRequest.clientId,
        contentType: ContentType.OTHER,
        extraData: {
            type: 'attestations',
            attestationType: paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT
                ? paymentRequest.preimbursementStatus
                    ? 'payment_request_preimbursement_upload_proof'
                    : 'payment_request_preimbursement'
                : requiresPreapprovalAttestation
                    ? 'payment_request_preapproval'
                    : 'payment_request',
        },
    });
    const [attestationContentIds, setAttestationContentIds] = useState([]);
    const [signature, setSignature] = useState('');
    const eligibilityLock = useMemo(() => {
        const eligibilityPolicy = getPolicy(RuleType.ELIGIBILITY, program);
        const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
        return checkEligibilityLock(paymentRequest, program, eligibilityPolicy, approvalPolicy);
    }, [getPolicy, program, paymentRequest]);
    const handleValidateRequest = () => {
        const errors = validatePaymentRequest(paymentRequest, t, getPolicy, program, formatCurrency);
        setRequestErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const validateAttestations = () => {
        const errors = {};
        if (attestationContents && attestationContents.length > 0) {
            if (!signature) {
                errors.signature = t('{field} is required', { values: { field: t('Signature') } });
            }
            else if (signature.toLocaleLowerCase() !==
                `${currentUser?.firstName} ${currentUser?.lastName}`.toLocaleLowerCase()) {
                errors.signature = t('Signature does not match your full name');
            }
            if (attestationContentIds.length !== attestationContents.length) {
                errors.attestations = t('Please accept all agreements');
            }
        }
        setRequestErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleValidateItem = () => {
        if (!itemToEdit)
            return false;
        const errors = validatePaymentRequestItem(itemToEdit, t, expensePolicy?.allowFeeTypes !== false, clientConfig?.timezone);
        setItemErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const updateItem = (itemToEdit) => {
        setPaymentRequest(prev => {
            let idx = prev.paymentRequestItems.findIndex(item => item.tempId === itemToEdit.tempId);
            if (idx === -1) {
                idx = prev.paymentRequestItems.length; // add to end of list if item not found
            }
            const items = [
                ...prev.paymentRequestItems.slice(0, idx),
                itemToEdit,
                ...prev.paymentRequestItems.slice(idx + 1),
            ];
            return {
                ...prev,
                paymentRequestItems: items,
            };
        });
        setItemToEdit(null);
    };
    const handleAddItem = (params = {}) => {
        if (!paymentRequest)
            return;
        setItemToEdit({
            id: 0,
            clientId: paymentRequest.clientId,
            paymentRequestId: 0,
            documents: { docs: [] },
            tempId: uniqueId('item-'),
            isDeleted: false,
            capYear: paymentRequest.capYear,
            capSegment: paymentRequest.capSegment,
            status: PaymentRequestItemStatus.PENDING,
            calculations: {},
            extraData: {},
            ...params,
        });
    };
    const handleItemChange = (paymentRequestItem) => {
        setItemToEdit(paymentRequestItem);
        // Clear errors for fields that have been modified
        if (itemToEdit) {
            const clearedErrors = { ...itemErrors };
            let hasChanges = false;
            // Check each field that might have changed and clear its error
            Object.keys(itemErrors).forEach(field => {
                if (itemToEdit[field] !== paymentRequestItem[field]) {
                    delete clearedErrors[field];
                    hasChanges = true;
                }
            });
            if (hasChanges) {
                setItemErrors(clearedErrors);
            }
        }
    };
    const handleItemRemove = (paymentRequestItem) => {
        if (!paymentRequest)
            return;
        setPaymentRequest(prev => ({
            ...prev,
            paymentRequestItems: compact(prev.paymentRequestItems.map(item => {
                if (item.tempId === paymentRequestItem.tempId) {
                    if (item.id === 0)
                        return null; // remove unsaved items
                    return {
                        ...item,
                        isDeleted: true,
                    };
                }
                return item;
            })),
        }));
    };
    const resetItemToEdit = () => {
        setItemToEdit(null);
        setItemErrors({});
    };
    const prepareRequestForSave = (props = {}) => {
        const request = omit(prepareForSave({ ...paymentRequest, ...props }), 'client', 'program', 'capYear', 'capSegment', 'budgetYear', 'budgetSegment', 'amountToPay', 'amountEarmarked', 'processedById', 'processedBy', 'submittedAt', 'datesSameAsProgram');
        // The refactored logic doesn't rely on the status being IN_REVIEW.
        // Employee facing statuses are computed on the server.
        // if (!eligibilityLock.locked || eligibilityLock.canSubmitExpenses) {
        //   request.status = PaymentRequestStatus.IN_REVIEW;
        // }
        return {
            ...request,
            paymentRequestItems: request.paymentRequestItems.map((item) => omit(item, 'feeType', 'capYear', 'capSegment', 'amountToPay', 'feeTypeName', 'calculations')),
            attestationContentIds,
        };
    };
    const save = async (props, afterSave = (_paymentRequest) => { }) => {
        try {
            const result = await savePaymentRequest({
                variables: {
                    input: prepareRequestForSave(omit(props, 'currentStep')),
                },
            });
            if (result.data?.savePaymentRequest?.success === false) {
                // show errors if any
                let messages = formatMessageDates(result.data.savePaymentRequest.messages);
                if ('validateOnly' in props &&
                    props.validateOnly &&
                    'currentStep' in props &&
                    props.currentStep !== PaymentRequestStep.EXPENSES) {
                    // remove the error for expenses required when validation is not performed on expenses step
                    messages = messages.filter(m => m.value !== 'Expenses are required');
                }
                const [fieldErrors, otherErrors] = partition(messages, m => m.params && m.params.field);
                if (fieldErrors.length > 0) {
                    setRequestErrors(fieldErrors.reduce((acc, m) => {
                        acc[m.params?.field] = t(m.value, { values: m.params });
                        return acc;
                    }, {}));
                }
                setErrors(otherErrors);
                return isEmpty(messages);
            }
            if (afterSave) {
                afterSave(result.data.savePaymentRequest.paymentRequest);
            }
            return true;
        }
        catch (e) {
            console.log(e);
            setErrors([
                {
                    value: t('Error creating payment request'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
            return false;
        }
    };
    return {
        paymentRequest,
        setPaymentRequest,
        requestErrors,
        setRequestErrors,
        itemErrors,
        itemToEdit,
        setItemToEdit,
        errors,
        setErrors,
        updateItem,
        handleValidateItem,
        handleValidateRequest,
        save,
        handleAddItem,
        handleItemChange,
        handleItemRemove,
        resetItemToEdit,
        attestationContents,
        attestationContentIds,
        setAttestationContentIds,
        signature,
        setSignature,
        validateAttestations,
        prepareRequestForSave,
        eligibilityLock,
    };
};
